import {
  Binoculars,
  ChartBar,
  Code,
  Cpu,
  Database,
  Handshake,
  MagnifyingGlass,
  PenNib,
  Plug,
  Table,
} from "@phosphor-icons/react"
import dynamic from "next/dynamic"
import { BASE_PATH } from "@/constants/app.mjs"
import FinancialStatement from "../components/common/illustrations/FinancialStatement"
import APIOutlineIllustration from "../components/home/APIOutlineIllustration"
import DataWarehouseOutlineIllustration from "../components/home/DataWarehouseOutlineIllustration"
import ExploreOutlineIllustration from "../components/home/ExploreOutlineIllustration"
import type { Feature } from "../components/home/FeatureCallouts"
import MCPOutlineIllustration from "../components/home/MCPOutlineIllustration"
import ResearchOutlineIllustration from "../components/home/ResearchOutlineIllustration"
import SheetsOutlineIllustration from "../components/home/SheetsOutlineIllustration"
import StudioOutlineIllustration from "../components/home/StudioOutlineIllustration"

// Only the first slide is in the initial HTML and bundle. The others load when the hero shows them
const T1Chip = dynamic(
  () => import("../components/common/illustrations/T1Chip")
)
const Top5Projects = dynamic(
  () => import("../components/common/illustrations/Top5Projects")
)
const Use = dynamic(() => import("../components/common/illustrations/Use"))

export const slidesById = {
  understand: {
    word: "understand",
    featuredContent: <FinancialStatement />,
    backgroundImage: `${BASE_PATH}/static/images/marketing/home/hero-illustration-bank.webp`,
    accentColor: "#DD995D",
    ctaText: "Learn more about Explorer",
    ctaHref: "/products/explorer",
  },
  trust: {
    word: "trust",
    featuredContent: <T1Chip />,
    backgroundImage: `${BASE_PATH}/static/images/marketing/home/hero-illustration-warehouse.webp`,
    accentColor: "#925BD6",
    ctaText: "Learn more about our data pipeline",
    ctaHref: "/about#B",
  },
  explore: {
    word: "explore",
    featuredContent: <Top5Projects />,
    backgroundImage: `${BASE_PATH}/static/images/marketing/home/hero-illustration-mountains.webp`,
    accentColor: "#61E7BF",
    ctaText: "Learn more about Explorer",
    ctaHref: "/products/explorer",
  },
  use: {
    word: "act on",
    featuredContent: <Use />,
    backgroundImage: `${BASE_PATH}/static/images/marketing/home/hero-illustration-construction.webp`,
    accentColor: "#5291C1",
    ctaText: "Learn more about our API",
    ctaHref: "/products/api",
  },
}
export const slides = Object.values(slidesById)

export const featureCallouts: Array<Feature> = [
  // Hidden features - uncomment to restore
  // {
  //   id: "industry-standard",
  //   title: "The industry standard for blockchain data",
  //   description:
  //     "We are on a mission to create the accounting and disclosure standards for the crypto market – and the analytics platform for institutional-grade due diligence.",
  //   ctaText: "Read more",
  //   ctaHref: "/about#industry-standard",
  //   image: `${BASE_PATH}/static/images/marketing/products/explorer/understand.jpg`,
  // },
  // {
  //   id: "full-stack",
  //   title: "A full stack onchain data platform",
  //   description:
  //     "We ensure the best possible data quality, granularity, and accuracy through our vertically integrated data supply chain.",
  //   ctaText: "Read more",
  //   ctaHref: "/about#full-stack",
  //   image: `${BASE_PATH}/static/images/marketing/home/callouts/ui_snippet_projects.webp`,
  // },
  // {
  //   id: "trustworthy-data",
  //   title: "Onchain data you can trust and act on",
  //   description:
  //     "We empower both institutional and retail investors with high-quality onchain data that serves as the foundation for informed decision-making.",
  //   ctaText: "Read more",
  //   ctaHref: "/about#onchain-data",
  //   image: `${BASE_PATH}/static/images/marketing/home/callouts/onchain.jpg`,
  // },
  {
    id: "bloomberg",
    title: "Available on Bloomberg Terminal",
    description:
      "The Token Terminal web application is now available on the Bloomberg Terminal App Portal. To access the application inside Bloomberg, run the command APPS TOKEN GO.",
    ctaText: "Read the announcement",
    ctaHref: "/articles/crypto-fundamentals-bloomberg-launch",
    image: `${BASE_PATH}/static/images/marketing/home/callouts/ui_snippet_bloomberg.webp`,
  },
]

export type ProductId =
  | "explorer"
  | "api"
  | "datashare"
  | "sheets"
  | "studio"
  | "mcp"
  | "research"
  | "integrations"
  | "data-partnerships"
  | "indexes"

export const products = [
  {
    id: "explorer",
    name: "Explorer",
    subheading: "Browse and compare",
    description:
      "Browse prebuilt dashboards, compare projects across chains, and analyze standardized financial metrics, all through an intuitive no-code interface.",
    ctaText: "Learn more",
    ctaHref: "/products/explorer",
    icon: Binoculars,
    visual: ExploreOutlineIllustration,
  },
  {
    id: "studio",
    name: "Studio",
    subheading: "Build and share",
    description:
      "Create custom charts, build shareable dashboards, and collaborate with your team on tailored analyses using Token Terminal's standardized data.",
    ctaText: "Learn more",
    ctaHref: "/products/studio",
    icon: PenNib,
    visual: StudioOutlineIllustration,
  },
  {
    id: "mcp",
    name: "MCP",
    subheading: "For AI & LLM applications",
    description:
      "Connect AI agents and LLMs to Token Terminal's onchain data through the Model Context Protocol.",
    ctaText: "Learn more",
    ctaHref: "/products/mcp",
    icon: Cpu,
    visual: MCPOutlineIllustration,
  },
  {
    id: "sheets",
    name: "Sheets",
    subheading: "For analysts & researchers",
    description:
      "Pull standardized onchain metrics directly into Google Sheets or Excel with custom formulas, no coding required.",
    ctaText: "Learn more",
    ctaHref: "/products/sheets",
    icon: Table,
    visual: SheetsOutlineIllustration,
  },
  {
    id: "api",
    name: "API",
    subheading: "For data scientists & developers",
    description:
      "Access standardized metrics programmatically via REST API to power your models, applications, and research pipelines.",
    ctaText: "Learn more",
    ctaHref: "/products/api",
    icon: Code,
    visual: APIOutlineIllustration,
  },
  {
    id: "datashare",
    name: "Datashare",
    subheading: "For technical teams",
    description:
      "Query the full data catalog, from raw blockchain data to decoded smart contracts and standardized metrics, directly in your data warehouse using SQL.",
    ctaText: "Learn more",
    ctaHref: "/products/datashare",
    icon: Database,
    visual: DataWarehouseOutlineIllustration,
  },
  {
    id: "research",
    name: "Research",
    subheading: "Custom research & data support",
    description:
      "A dedicated research team that turns Token Terminal's full data warehouse into custom analysis, dashboards, and reports – tailored for you.",
    ctaText: "Learn more",
    ctaHref: "/products/research",
    icon: MagnifyingGlass,
    visual: ResearchOutlineIllustration,
  },
  {
    id: "data-partnerships",
    name: "Data Partnerships",
    subheading: "Protocols & dApps",
    description:
      "How we partner with protocols and dApps to provide high-quality onchain data.",
    ctaText: "Learn more",
    ctaHref: "/products/data-partnerships",
    icon: Handshake,
    visual: DataWarehouseOutlineIllustration,
  },
  {
    id: "integrations",
    name: "Integrations",
    subheading: "Third-party platforms",
    description:
      "See how leading exchanges, data platforms, and AI tools use Token Terminal data to serve 250+ million users daily.",
    ctaText: "Explore integrations",
    ctaHref: "/products/integrations",
    icon: Plug,
    visual: DataWarehouseOutlineIllustration,
  },
  {
    id: "indexes",
    name: "Indexes",
    subheading: "Fundamentals-weighted",
    description:
      "Fundamentals-weighted crypto indexes powered by Token Terminal data.",
    ctaText: "Learn more",
    ctaHref: "/products/indexes",
    icon: ChartBar,
    visual: DataWarehouseOutlineIllustration,
  },
] satisfies { id: ProductId; [key: string]: unknown }[]

export type Testimonial = {
  quote: string
  name: string
  company: string
  avatar: string
  socialHandle?: string
}

export const groupedTestimonials: Testimonial[][] = [
  [
    {
      quote:
        "Even though the industry is open source and most of the information is publicly available on the blockchain, it can still be a lot of work to get accurate and actionable data. We’ve found that Token Terminal is consistently putting out high quality data, with everything clearly documented.\n\nThey’ve really changed the way we think about onchain data internally, since we don’t need to build our data pipelines from scratch anymore.",
      name: "Felipe Montealegre",
      company: "Theia",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-TheiaResearch.jpg`,
      socialHandle: "TheiaResearch",
    },
    {
      quote:
        "We’re frequent and happy users of the data and dashboards available on Token Terminal. Token Terminal has done a great job focusing on standardizing KPIs that make fundamental analysis of digital assets faster and easier.",
      name: "Cosmo Jiang",
      company: "Pantera",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-cosmo_jiang.png`,
      socialHandle: "cosmo_jiang",
    },
    {
      quote:
        "Token Terminal has been an invaluable tool in my crypto research, offering deep insights into on-chain metrics and token economics. Its user-friendly interface and robust data sets streamline my analysis, ensuring I stay ahead in the rapidly evolving crypto landscape. A must-have for anyone serious about crypto analysis.",
      name: "Ryan Rasmussen",
      company: "Bitwise",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-RasterlyRock.jpg`,
      socialHandle: "RasterlyRock",
    },
  ],

  [
    {
      quote:
        "Token Terminal's decoders turn raw onchain chaos into structured, instruction-level insights—giving every builder the deep microstructure transparency once reserved for sophisticated teams. I look forward to seeing how the Solana community uses these powerful new primitives.",
      name: "Devan Mitchem",
      company: "Google",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-devan-mitchem.png`,
    },
    {
      quote:
        "At CF Benchmarks we have found Token Terminal to be the best of breed of blockchain data providers. First and foremost their data is accurate and comprehensive, of imperative importance to a UK FCA regulated benchmark administrator like ourselves. On top of that the data interrogation tools and APIs across all their products are reliable, intuitive and performant. Hard to ask for more.",
      name: "Sui Chung",
      company: "CF Benchmarks",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-CFBenchmarks.jpg`,
      socialHandle: "CFBenchmarks",
    },
    {
      quote:
        "As a crypto investor, I spend a lot of time trying to understand the financial performance of crypto protocols. Before Token Terminal, this meant a lot of manual work across different platforms, whereas now I’m able to access comparable metrics for hundreds of protocols all in one place. At this point, I can’t imagine doing due diligence without the Terminal.",
      name: "Sanat Kapur",
      company: "Dragonfly Capital",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-kapursanat.png`,
      socialHandle: "kapursanat",
    },
    {
      quote:
        "Token Terminal saves my team hours of work by aggregating information across multiple data sources. Having revenue data for projects across multiple sectors and chains all in one place is invaluable.",
      name: "Katie Talati",
      company: "Arca",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-KatieTalati.jpg`,
      socialHandle: "KatieTalati",
    },
  ],
  [
    {
      quote:
        "Token Terminal gives me clean, accurate, and up-to-date data to quickly understand what’s happening in the markets and how to think about valuing assets. If you’re interested or working in investing, it’s invaluable.",
      name: "Marcos Veremis",
      company: "Accolade Partners",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-marcos-vernemis.jpg`,
      socialHandle: "AccoladePrtnrs",
    },
    {
      quote:
        "Token Terminal has been instrumental in revolutionizing the way we approach on-chain data for our index business. Their commitment to delivering high-quality, actionable insights has made a significant impact on our joint project to create the first fundamental indexes for crypto.\n\nWhat stands out most is their ability to distill complex data into comprehensible formats, enabling portfolio managers to make informed decisions with ease. The clarity and precision that Token Terminal brings to the table are unparalleled, making them an invaluable partner in navigating the ever-evolving blockchain landscape.",
      name: "Martin Leinweber",
      company: "MarketVector Indexes",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-mleinweber2.jpg`,
      socialHandle: "mleinweber2",
    },
    {
      quote:
        "Token Terminal gives me access to high quality and well documented metrics for a broad range of projects and networks allowing me to go straight to analyzing data.",
      name: "Matt Maximo",
      company: "Van Eck",
      avatar: `${BASE_PATH}/static/images/marketing/home/testimonials/avatar-matt-maximo.jpg`,
    },
  ],
]
